import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LuEye, LuEyeOff, LuLoaderCircle, LuCircleAlert } from 'react-icons/lu';
import AuthShell from '../components/AuthShell';
import { apiFetch } from '../services/api';
import { destinoDentroDoApp } from '../config/boasVindas';

interface Convite { nome: string; email: string; empresa: string }

/**
 * /aceitar-convite?token=… — o destino do link do e-mail de convite (05/10/2026).
 *
 * O admin não digita mais a senha de ninguém: convida por e-mail e a pessoa cria a
 * própria senha aqui. Anônima, como a redefinição: o e-mail pode ser aberto em
 * outro navegador. Diferente da redefinição, ENTRA direto ao salvar — a conta
 * nunca teve senha, então não há sessão alheia para derrubar.
 */
const AceitarConvitePage = () => {
  const apiUrl = import.meta.env.VITE_API_URL;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [convite, setConvite] = useState<Convite | null>(null);
  const [conferindo, setConferindo] = useState(true);
  const [erroConvite, setErroConvite] = useState<string | null>(null);

  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  /* StrictMode monta duas vezes em desenvolvimento; a consulta é só leitura, mas
     não precisa ir duas vezes. */
  const jaConferiu = useRef(false);

  useEffect(() => {
    if (jaConferiu.current || !token) { setConferindo(false); return; }
    jaConferiu.current = true;

    (async () => {
      try {
        const r = await apiFetch(`${apiUrl}/api/Auth/Convite/${encodeURIComponent(token)}`);
        const d = await r.json().catch(() => ({}));
        if (!r.ok || d.sucesso === false) {
          setErroConvite(d.mensagem || d.message || 'Este convite não é mais válido.');
          return;
        }
        setConvite(d.dados as Convite);
      } catch {
        setErroConvite('Não conseguimos falar com o servidor. Tente abrir o link de novo.');
      } finally {
        setConferindo(false);
      }
    })();
  }, [apiUrl, token]);

  /* Mesma regra do back (Senha.ValidarComplexidade) e da RedefinirSenhaPage. */
  const temTamanho = senha.length >= 8;
  const temMaiuscula = /[A-Z]/.test(senha);
  const temMinuscula = /[a-z]/.test(senha);
  const temEspecial = /[^A-Za-z0-9]/.test(senha);
  const criteriosOk = [temTamanho, temMaiuscula, temMinuscula, temEspecial].filter(Boolean).length;
  const senhaValida = criteriosOk === 4;

  const ajuda = (): string => {
    if (!senha) return 'Mínimo de 8 caracteres, com maiúscula, minúscula e um símbolo.';
    if (senhaValida) return 'Boa senha.';
    const faltando: string[] = [];
    if (!temTamanho) faltando.push('8 caracteres');
    if (!temMaiuscula) faltando.push('uma maiúscula');
    if (!temMinuscula) faltando.push('uma minúscula');
    if (!temEspecial) faltando.push('um símbolo');
    const lista = faltando.length === 1
      ? faltando[0]
      : `${faltando.slice(0, -1).join(', ')} e ${faltando[faltando.length - 1]}`;
    return `Falta ${lista}.`;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (carregando) return;

    if (!senhaValida) {
      setErro(ajuda());
      document.getElementById('conv-senha')?.focus();
      return;
    }

    setErro(null);
    setCarregando(true);

    try {
      const resposta = await apiFetch(`${apiUrl}/api/Auth/AceitarConvite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, senha }),
      });
      const dados = await resposta.json().catch(() => ({}));

      if (!resposta.ok || dados.sucesso === false) {
        setErro(dados.mensagem || dados.message || 'Este convite não é mais válido.');
        return;
      }

      /* A API já gravou os cookies da sessão. Se não conseguiu, a senha existe:
         é só entrar com ela. */
      if (dados.dados?.logado === false) {
        navigate('/login?senha=1', { replace: true });
        return;
      }

      const me = await apiFetch(`${apiUrl}/api/Auth/Me`);
      if (!me.ok) { navigate('/login?senha=1', { replace: true }); return; }
      localStorage.setItem('user', JSON.stringify(await me.json()));
      navigate(destinoDentroDoApp(), { replace: true });
    } catch {
      setErro('Não conseguimos falar com o servidor. Tente de novo em instantes.');
    } finally {
      setCarregando(false);
    }
  };

  if (!token) {
    return (
      <AuthShell>
        <h1 className="au-h1">Link inválido</h1>
        <p className="au-sub">Este endereço não tem o código do convite. Abra o link que chegou no seu e-mail.</p>
        <Link to="/login" className="au-btn" style={{ textDecoration: 'none' }}>
          Ir para o login
        </Link>
      </AuthShell>
    );
  }

  if (conferindo) {
    return (
      <AuthShell>
        <h1 className="au-h1">Conferindo o convite…</h1>
        <p className="au-sub"><LuLoaderCircle size={16} className="au-spin" /> Só um instante.</p>
      </AuthShell>
    );
  }

  if (erroConvite || !convite) {
    return (
      <AuthShell>
        <h1 className="au-h1">Convite indisponível</h1>
        <div className="au-alert is-danger" role="alert">
          <LuCircleAlert size={16} />
          <span>{erroConvite ?? 'Este convite não é mais válido.'}</span>
        </div>
        <p className="au-sub">Se você já criou sua senha, é só entrar. Se o convite venceu, peça um novo para quem convidou você.</p>
        <Link to="/login" className="au-btn" style={{ textDecoration: 'none' }}>
          Ir para o login
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="au-h1">Bem-vindo, {convite.nome.split(' ')[0]}!</h1>
      <p className="au-sub">
        Você foi convidado para a equipe <strong>{convite.empresa}</strong>. Crie a sua senha para entrar.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        {erro && (
          <div className="au-alert is-danger" role="alert">
            <LuCircleAlert size={16} />
            <span>{erro}</span>
          </div>
        )}

        <div className="au-group">
          <label className="au-label" htmlFor="conv-email">Seu acesso</label>
          <div className="au-input-wrap">
            <input id="conv-email" className="au-input" type="email" value={convite.email} readOnly disabled />
          </div>
        </div>

        <div className="au-group">
          <label className="au-label" htmlFor="conv-senha">Crie uma senha</label>
          <div className="au-input-wrap has-toggle">
            <input
              id="conv-senha"
              className={`au-input${erro ? ' is-error' : ''}`}
              type={mostrarSenha ? 'text' : 'password'}
              placeholder="••••••••"
              value={senha}
              autoComplete="new-password"
              autoFocus
              onChange={ev => { setSenha(ev.target.value); if (erro) setErro(null); }}
            />
            <button
              type="button"
              className="au-toggle"
              onClick={() => setMostrarSenha(v => !v)}
              aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {mostrarSenha ? <LuEyeOff size={16} /> : <LuEye size={16} />}
            </button>
          </div>
          <div className="au-meter" aria-hidden="true">
            {[0, 1, 2, 3].map(i => (
              <span
                key={i}
                className={criteriosOk > i ? (criteriosOk === 1 && senha ? 'is-weak' : 'is-on') : ''}
              />
            ))}
          </div>
          <p className="au-help">{ajuda()}</p>
        </div>

        <button type="submit" className={`au-btn${carregando ? ' is-loading' : ''}`}>
          {carregando ? (
            <><LuLoaderCircle size={16} className="au-spin" /> Entrando…</>
          ) : (
            'Criar senha e entrar'
          )}
        </button>
      </form>
    </AuthShell>
  );
};

export default AceitarConvitePage;
